import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import StarField from "./StarField";
import CloudLayer from "./CloudLayer";

/**
 * LayeredBackground
 * Renders two non-interactive layers:
 *  - Sky (full-bleed)
 *  - Foreground (buildings), anchored to the bottom
 *
 * Props:
 *  - skySrc: string (required)
 *  - foregroundSrc: string (required)
 *  - overlay?: string | false  (optional CSS color like 'rgba(0,0,0,.25)')
 *  - height?: string | number  (default: '100vh')
 */
export default function LayeredBackground({
  skySrc,
  foregroundSrc,
  mobileBg,
  overlay = false,
  height = "100vh",
  showForeground = true,
  showStars = true,
  showClouds = true,
}) {
  const isMobile = useMediaQuery("(max-width:900px)");

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        height,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {/* SKY */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${isMobile ? mobileBg : skySrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: 0,
        }}
      />

      {/* OPTIONAL OVERLAY */}
      {overlay && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background: overlay,
          }}
        />
      )}

      {/* FOREGROUND / BUILDINGS */}
      {showForeground && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            // Foreground is tall and anchored; we scale a bit more on desktop
            height: { xs: "100%", md: "100%" },
            backgroundImage: `url(${isMobile ? "" : foregroundSrc})`,
            backgroundSize: "cover",
            backgroundPosition: "bottom center",
            backgroundRepeat: "no-repeat",
            // Prep for future parallax (kept static for now)
            willChange: "transform",
            transform: "translateZ(0)",
            imageRendering: isMobile ? "auto" : "auto",
            zIndex: 3,
          }}
        />
      )}
      {showStars && <StarField count={10} maxSize={2} zIndex={1} />}

      {/* 2. Clouds (subtle drift) */}
      {showClouds && (
        <CloudLayer rows={2} density={2} minSpeed={50} maxSpeed={80} zIndex={2} />
      )}
    </Box>
  );
}
